<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import HighRiskConfirmation from '../components/access/HighRiskConfirmation.vue'
import PermissionMatrix from '../components/access/PermissionMatrix.vue'
import RoleAssigneesPanel from '../components/access/RoleAssigneesPanel.vue'
import RoleList from '../components/access/RoleList.vue'
import { apiErrorMessage, createAccessRole, deleteAccessRole, getAccessPermissions, getAccessRole, getAccessRoles, updateAccessRole } from '../api'
import { roleCode } from '../session'
import { useI18n } from '../i18n'
import { permissionName, systemRoleName } from '../access/labels.js'

const loading = ref(true)
const saving = ref(false)
const error = ref('')
const roles = ref([])
const groups = ref([])
const selected = ref(null)
const editing = ref(false)
const riskConfirmationOpen = ref(false)
const pendingHighRisk = ref([])
const isAdmin = computed(() => roleCode() === 'ADMIN')
const form = reactive({ displayName: '', description: '', permissionKeys: [], version: 0 })
const { t } = useI18n()

async function load(selectId) {
  loading.value = true
  error.value = ''
  try {
    const [rolePage, permissionPage] = await Promise.all([getAccessRoles({ pageSize: 100 }), getAccessPermissions()])
    roles.value = rolePage.items
    groups.value = permissionPage.groups
    const id = selectId ?? selected.value?.id ?? roles.value[0]?.id
    if (id) await selectRole({ id })
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  } finally {
    loading.value = false
  }
}

async function selectRole(role) {
  editing.value = false
  error.value = ''
  try {
    selected.value = (await getAccessRole(role.id)).role
    Object.assign(form, { displayName: selected.value.displayName, description: selected.value.description,
      permissionKeys: [...selected.value.selectedPermissionKeys], version: selected.value.version })
  } catch (cause) {
    error.value = apiErrorMessage(t, cause)
  }
}

function startCreate() {
  selected.value = null
  editing.value = true
  Object.assign(form, { displayName: '', description: '', permissionKeys: [], version: 0 })
}

function startEdit() {
  editing.value = true
  Object.assign(form, { displayName: selected.value.displayName, description: selected.value.description,
    permissionKeys: [...selected.value.selectedPermissionKeys], version: selected.value.version })
}

async function saveRole() {
  if (form.displayName.trim().length < 2) { error.value = t('roleNameTooShort'); return }
  const highRisk = groups.value.flatMap((group) => group.permissions)
    .filter((permission) => permission.risk === 'high' && form.permissionKeys.includes(permission.key))
  if (highRisk.length) {
    pendingHighRisk.value = highRisk
    riskConfirmationOpen.value = true
    return
  }
  await persistRole([])
}

async function confirmHighRisk() {
  const highRisk = [...pendingHighRisk.value]
  riskConfirmationOpen.value = false
  pendingHighRisk.value = []
  await persistRole(highRisk)
}

async function persistRole(highRisk) {
  saving.value = true
  error.value = ''
  const payload = { displayName: form.displayName.trim(), description: form.description.trim(),
    permissionKeys: form.permissionKeys, highRiskConfirmation: highRisk.length
      ? { confirmed: true, permissionKeys: highRisk.map((permission) => permission.key) } : null }
  try {
    const result = selected.value
      ? await updateAccessRole(selected.value.id, { ...payload, version: form.version })
      : await createAccessRole(payload)
    editing.value = false
    await load(result.role.id)
  } catch (cause) {
    if (cause.code === 'version_conflict') {
      const roleId = selected.value.id
      await load(roleId)
      error.value = t('roleVersionConflict')
    } else {
      error.value = apiErrorMessage(t, cause)
    }
  } finally { saving.value = false }
}

async function removeRole() {
  if (!selected.value || !window.confirm(t('deleteRoleConfirm', { name: selected.value.displayName }))) return
  try { await deleteAccessRole(selected.value.id, selected.value.version); selected.value = null; await load() }
  catch (cause) { error.value = apiErrorMessage(t, cause) }
}

onMounted(() => load())
</script>

<template><section class="access-roles-page"><header class="access-page-header"><div><p class="eyebrow">{{ t('accessManagement') }}</p><h1>{{ t('rolesAndPermissions') }}</h1><p>{{ t('rolesIntro') }}</p></div><button v-if="isAdmin" class="button access-primary" type="button" :disabled="loading" @click="startCreate">{{ t('createRole') }}</button></header>
  <p v-if="!isAdmin" class="notice info">{{ t('rolesReadOnly') }}</p><p v-if="error" class="notice error" role="alert">{{ error }}</p><p v-if="loading" class="access-loading">{{ t('loadingRoles') }}</p>
  <div v-else class="role-workspace"><RoleList :roles="roles" :selected-id="selected?.id" @select="selectRole" />
    <main class="role-detail-card"><template v-if="editing"><header><div><p class="eyebrow">{{ selected ? t('customRole') : t('newRole') }}</p><h2>{{ selected ? t('editRole') : t('createRoleTitle') }}</h2></div></header><div class="role-editor-fields"><label>{{ t('roleName') }}<input v-model="form.displayName" maxlength="80" /></label><label>{{ t('roleDescription') }}<textarea v-model="form.description" maxlength="500" rows="3"></textarea></label></div><h3>{{ t('permissionMatrix') }}</h3><PermissionMatrix v-model:selected="form.permissionKeys" :groups="groups" :readonly="false" /><footer><button class="button button-secondary" type="button" @click="selected ? selectRole(selected) : load()">{{ t('cancel') }}</button><button class="button access-primary" type="button" :disabled="saving" @click="saveRole">{{ saving ? t('savingAction') : t('saveRole') }}</button></footer></template>
      <template v-else-if="selected"><header><div><p class="eyebrow">{{ selected.isSystem ? t('systemRole') : t('customRole') }}</p><h2>{{ systemRoleName(t, selected) }}</h2><p v-if="!selected.isSystem">{{ selected.description }}</p></div><div v-if="isAdmin && !selected.isSystem" class="role-header-actions"><button class="button button-secondary" type="button" @click="startEdit">{{ t('editAction') }}</button><button class="button button-danger" type="button" @click="removeRole">{{ t('delete') }}</button></div></header><p v-if="selected.isSystem" class="system-role-notice"><strong>{{ t('systemRoleNotice') }}</strong><span>{{ t('systemRoleImmutableHint') }}</span></p><h3>{{ t('permissionMatrix') }}</h3><PermissionMatrix :groups="groups" :selected="selected.effectivePermissionKeys" :readonly="true" /><div class="station-role-note"><strong>{{ t('stationAccess') }}</strong><p>{{ t('stationScopePerUser') }}</p></div></template>
      <div v-else class="access-empty"><h2>{{ t('createCustomRoleEmpty') }}</h2><p>{{ t('createCustomRoleHint') }}</p></div></main>
    <RoleAssigneesPanel :assignees="selected?.assignees?.items ?? []" /></div></section>
    <HighRiskConfirmation v-if="riskConfirmationOpen" :permission-names="pendingHighRisk.map((permission) => permissionName(t, permission))"
      @confirm="confirmHighRisk" @cancel="riskConfirmationOpen = false; pendingHighRisk = []" /></template>
